import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { Formik } from 'formik';
import Select from 'react-select';
import { FaSearch } from 'react-icons/fa';
import moment from 'moment';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import { filtrosSchema } from './validationSchemas';

const obterHoje = () => moment().format('YYYY-MM-DD');

const FormFiltrosInner = ({
  values, errors, touched, setFieldValue, handleChange, handleBlur, handleSubmit,
  grupos, contas, opcoesGrupo, opcoesContas, onGrupoChange, loading,
}) => {
  const autoSelecionouRef = useRef(false);

  // Auto-seleção de GE única (MAPEAMENTO_GLOBAIS §3.2)
  useEffect(() => {
    if (grupos.length === 1 && !autoSelecionouRef.current) {
      autoSelecionouRef.current = true;
      const ge = grupos[0].codigo_grupo_empresa;
      setFieldValue('codigoGrupoEmpresa', ge);
      onGrupoChange(ge);
    }
  }, [grupos, setFieldValue, onGrupoChange]);

  return (
    <Form onSubmit={handleSubmit}>
      <Card className="mb-3">
        <Card.Header>Filtros</Card.Header>
        <Card.Body>
          <Row>
            <Col md={4} className="mb-2">
              <Form.Label>Grupo Empresa</Form.Label>
              <Select
                styles={selectStylesSm}
                options={opcoesGrupo}
                placeholder="Selecione..."
                value={opcoesGrupo.find((o) => o.value === values.codigoGrupoEmpresa) || null}
                onChange={(opt) => {
                  const val = opt ? opt.value : '';
                  setFieldValue('codigoGrupoEmpresa', val);
                  setFieldValue('codigoConta', '');
                  onGrupoChange(val);
                }}
                onBlur={() => handleBlur({ target: { name: 'codigoGrupoEmpresa' } })}
              />
              {touched.codigoGrupoEmpresa && errors.codigoGrupoEmpresa && (
                <small className="text-danger">{errors.codigoGrupoEmpresa}</small>
              )}
            </Col>

            <Col md={4} className="mb-2">
              <Form.Label>Conta</Form.Label>
              <Select
                styles={selectStylesSm}
                options={opcoesContas}
                placeholder={values.codigoGrupoEmpresa ? 'Selecione...' : 'Selecione a GE primeiro'}
                isDisabled={!values.codigoGrupoEmpresa}
                value={opcoesContas.find((o) => o.value === values.codigoConta) || null}
                onChange={(opt) => setFieldValue('codigoConta', opt ? opt.value : '')}
                onBlur={() => handleBlur({ target: { name: 'codigoConta' } })}
              />
              {touched.codigoConta && errors.codigoConta && (
                <small className="text-danger">{errors.codigoConta}</small>
              )}
            </Col>

            <Col md={2} className="mb-2">
              <Form.Label>Data Contábil</Form.Label>
              <Form.Control
                type="date"
                name="dataContabil"
                size="sm"
                value={values.dataContabil}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.dataContabil && !!errors.dataContabil}
              />
              {touched.dataContabil && errors.dataContabil && (
                <Form.Control.Feedback type="invalid">
                  {errors.dataContabil}
                </Form.Control.Feedback>
              )}
            </Col>

            <Col md={2} className="mb-2 d-flex flex-column justify-content-end">
              <Form.Check
                type="checkbox"
                id="listarSaldoZerado"
                name="listarSaldoZerado"
                label="Listar saldo zerado"
                checked={values.listarSaldoZerado}
                onChange={handleChange}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={loading}
              >
                <FaSearch className="mr-1" />
                {loading ? 'Buscando...' : 'Buscar'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Form>
  );
};

const FormFiltros = ({ grupos, contas, onGrupoChange, onSubmit, loading }) => {
  const opcoesGrupo = grupos.map((g) => ({
    value: g.codigo_grupo_empresa,
    label: `${g.codigo_grupo_empresa} - ${g.apelido}`,
  }));

  const opcoesContas = contas.map((c) => ({
    value: c.codigo_conta,
    label: `${c.codigo_conta} - ${c.apelido_conta}`,
  }));

  return (
    <Formik
      initialValues={{
        codigoGrupoEmpresa: '',
        codigoConta: '',
        dataContabil: obterHoje(),
        listarSaldoZerado: false,
      }}
      validationSchema={filtrosSchema}
      onSubmit={(values) => onSubmit(values)}
      enableReinitialize={false}
    >
      {(formikProps) => (
        <FormFiltrosInner
          {...formikProps}
          grupos={grupos}
          contas={contas}
          opcoesGrupo={opcoesGrupo}
          opcoesContas={opcoesContas}
          onGrupoChange={onGrupoChange}
          loading={loading}
        />
      )}
    </Formik>
  );
};

FormFiltros.propTypes = {
  grupos: PropTypes.arrayOf(PropTypes.shape({
    codigo_grupo_empresa: PropTypes.number,
    apelido: PropTypes.string,
  })).isRequired,
  contas: PropTypes.arrayOf(PropTypes.shape({
    codigo_conta: PropTypes.number,
    apelido_conta: PropTypes.string,
  })).isRequired,
  onGrupoChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

FormFiltros.defaultProps = {
  loading: false,
};

export default FormFiltros;
